import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Course } from '../../../../../../models/interfaces/course.interface';
import { CourseSection } from '../../../../../../models/interfaces/course_section.interface';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../../core/services/toast/toast.service';
import { cloneDeep } from 'lodash-es';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-modal-clone-exam',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MatProgressSpinnerModule
    ],
    templateUrl: './modal-clone-exam.component.html',
    styleUrl: './modal-clone-exam.component.scss'
})
export class ModalCloneExamComponent implements OnInit, OnDestroy {
  examId!: string;
  examName!: string;
  showCloseAlert = false;
  examForm: FormGroup = new FormGroup({});
  isSavingExam = false;
  courses: Array<Course> = [];
  sections: Array<CourseSection> = [];

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalCloneExamComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    this.courses = receivedData?.courses || [];
    this.examId = receivedData?.examId;
    this.examName = receivedData?.examName;
    const courseId = receivedData?.courseId;
    if (!this.courses.length || !this.examId || !this.examName || !courseId) {
      this.closeModal();
      return;
    }

    this.examForm = new FormGroup({
      name: new FormControl(null, Validators.required),
      courseId: new FormControl(courseId, Validators.required),
      sectionId: new FormControl(receivedData?.sectionId || null)
    });

    this.setSections(courseId);
  }

  listenForChanges(): void {
    this.examForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
      this.setSections(value.courseId);
    });
  }

  setSections(courseId: string): void {
    if (!courseId) {
      this.sections = [];
      return;
    }

    const courseIndex = this.courses.findIndex((course) => course.courseId === courseId);
    if (courseIndex === -1) return;

    this.sections = cloneDeep(this.courses[courseIndex].sections);
  }

  async cloneExam(): Promise<void> {
    try {
      this.isSavingExam = true;
      const formValue = this.examForm.value;
      const body = {
        examId: this.examId,
        name: formValue.name,
        courseId: formValue.courseId,
        sectionId: formValue.sectionId
      };
      const response = await firstValueFrom(this.api.cloneExam(body));
      this.isSavingExam = false;
      this.toast.presentSuccessToast(response.message);
      this.closeModal({ examUpdated: response.examUpdated });
    } catch (errorResponse: any) {
      this.isSavingExam = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error clonando examen');
    }
  }
}
